/**
 * NMS Gender Toggle
 * App-free implementation using Shopify's native tag-filtered collection URLs
 * + the Section Rendering API. No custom app and no Storefront API token.
 *
 * Data flow:
 *   1. Liquid (snippets/section) exposes the current product context, including
 *      the handleized family tag and opposite-gender tag.
 *   2. JS fetches:
 *        /collections/{handle}/{familyTag}+{oppositeTag}?section_id=nms-gender-toggle-data
 *      Shopify renders that collection filtered to those tags and returns a
 *      JSON list of products (handle, tags, availability, color metafield).
 *   3. JS runs the matching engine (handle priority -> colour score -> family).
 *
 * Architecture mirrors nms-explore-more-drawer.js:
 *   - ES6 classes, private class fields
 *   - async/await + fetch()
 *   - shared request cache (memory + sessionStorage)
 *   - pending request deduplication
 *   - small, single-purpose methods
 *
 * Matching priority:
 *   1. Exact opposite handle
 *   2. Same family tag + opposite gender + same colour
 *   3. Highest colour-overlap score
 *   4. First available product in the family with the opposite gender
 *   5. No match -> hide the toggle
 */
(function () {
  'use strict';

  var TAG_NAME = 'nms-gender-toggle';
  if (window.customElements && window.customElements.get(TAG_NAME)) return;

  /* ------------------------------------------------------------------ *
   * Constants
   * ------------------------------------------------------------------ */

  const CACHE_PREFIX = 'nms:gender-toggle:v2:';
  const DEFAULT_COLLECTION = 'all';
  const DEFAULT_DATA_SECTION = 'nms-gender-toggle-data';
  const DEFAULT_TTL_MINUTES = 30;
  const MAX_RETRIES = 2;
  const RETRY_BASE_DELAY = 250;
  const MAX_HANDLE_CANDIDATES = 4;
  const DATA_SCRIPT_ID = 'nms-gender-data';

  const MEN_TO_WOMEN = {
    men: 'women',
    mens: 'womens',
    man: 'woman',
    male: 'female',
    boy: 'girl',
    boys: 'girls',
    gents: 'ladies',
    him: 'her',
    his: 'hers'
  };

  const WOMEN_TO_MEN = Object.keys(MEN_TO_WOMEN).reduce(function (map, key) {
    map[MEN_TO_WOMEN[key]] = key;
    return map;
  }, {});

  /* ------------------------------------------------------------------ *
   * Utilities
   * ------------------------------------------------------------------ */

  class NmsUtils {
    static sleep(ms) {
      return new Promise(function (resolve) {
        setTimeout(resolve, ms);
      });
    }

    static hash(value) {
      let hash = 5381;
      for (let i = 0; i < value.length; i += 1) {
        hash = ((hash << 5) + hash + value.charCodeAt(i)) | 0;
      }
      return (hash >>> 0).toString(36);
    }

    static toBoolean(value, fallback) {
      if (value === undefined || value === null || value === '') return fallback;
      return value === true || value === 'true' || value === '1';
    }

    static toNumber(value, fallback) {
      const parsed = parseInt(value, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
    }

    static unique(list) {
      return Array.from(new Set(list));
    }

    static trimSlashes(value) {
      return String(value || '').replace(/^\/+|\/+$/g, '');
    }

    static joinUrl(root, path) {
      const base = String(root || '/').replace(/\/+$/, '');
      return base + '/' + String(path).replace(/^\/+/, '');
    }
  }

  /* ------------------------------------------------------------------ *
   * Colour parsing + scoring
   * ------------------------------------------------------------------ */

  class NmsColor {
    static normalize(value) {
      return String(value)
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[^a-z0-9]+/g, '');
    }

    /** Colour metafield: array, JSON string, or delimited string. */
    static fromMetafield(raw) {
      if (!raw) return [];

      let parts = [];

      if (Array.isArray(raw)) {
        parts = raw;
      } else if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (trimmed.charAt(0) === '[') {
          try {
            const parsed = JSON.parse(trimmed);
            parts = Array.isArray(parsed) ? parsed : [trimmed];
          } catch (error) {
            parts = [trimmed];
          }
        } else {
          parts = [trimmed];
        }
      } else {
        return [];
      }

      const tokens = [];
      parts.forEach(function (part) {
        String(part)
          .split(/[,/|&+]| and /i)
          .forEach(function (chunk) {
            const token = NmsColor.normalize(chunk);
            if (token) tokens.push(token);
          });
      });

      return NmsUtils.unique(tokens);
    }

    /** Fallback: colour sits after the last ":" in the title. */
    static fromTitle(title) {
      if (!title || title.indexOf(':') === -1) return [];

      const tail = title.slice(title.lastIndexOf(':') + 1);
      const tokens = [];

      tail
        .split(/[,/|&+]| and /i)
        .forEach(function (chunk) {
          const whole = NmsColor.normalize(chunk);
          if (whole) tokens.push(whole);

          chunk
            .trim()
            .split(/\s+/)
            .forEach(function (word) {
              const token = NmsColor.normalize(word);
              if (token && token.length > 2) tokens.push(token);
            });
        });

      return NmsUtils.unique(tokens);
    }

    static resolve(product) {
      const fromMetafield = NmsColor.fromMetafield(product ? product.colors : null);
      if (fromMetafield.length) return fromMetafield;
      return NmsColor.fromTitle(product ? product.title : '');
    }

    /** Overlap score: one point per shared colour. */
    static score(current, candidate) {
      if (!current.length || !candidate.length) return 0;

      const pool = new Set(candidate);
      let score = 0;

      NmsUtils.unique(current).forEach(function (color) {
        if (pool.has(color)) score += 1;
      });

      return score;
    }

    static isExactMatch(current, candidate) {
      if (!current.length || current.length !== candidate.length) return false;
      const pool = new Set(candidate);
      return current.every(function (color) {
        return pool.has(color);
      });
    }
  }

  /* ------------------------------------------------------------------ *
   * Handle transforms
   * ------------------------------------------------------------------ */

  class NmsHandle {
    static opposites(handle, gender) {
      if (!handle) return [];

      const map = gender === 'men' ? MEN_TO_WOMEN : WOMEN_TO_MEN;
      const tokens = String(handle).split('-');
      const indexes = [];

      tokens.forEach(function (token, index) {
        if (Object.prototype.hasOwnProperty.call(map, token)) indexes.push(index);
      });

      if (!indexes.length) return [];

      const candidates = new Set();

      // Replace every gendered token.
      candidates.add(
        tokens
          .map(function (token) {
            return map[token] || token;
          })
          .join('-')
      );

      // Replace only the first, then only the last gendered token.
      [indexes[0], indexes[indexes.length - 1]].forEach(function (index) {
        const copy = tokens.slice();
        copy[index] = map[copy[index]];
        candidates.add(copy.join('-'));
      });

      candidates.delete(handle);

      return Array.from(candidates).slice(0, MAX_HANDLE_CANDIDATES);
    }
  }

  /* ------------------------------------------------------------------ *
   * Collection client (Section Rendering API)
   * ------------------------------------------------------------------ */

  class NmsCollectionClient {
    static #memory = new Map();
    static #pending = new Map();

    #rootUrl;
    #collectionHandle;
    #dataSectionId;
    #ttl;
    #debug;
    #scope;

    constructor(options) {
      this.#rootUrl = options.rootUrl || '/';
      this.#collectionHandle = NmsUtils.trimSlashes(options.collectionHandle || DEFAULT_COLLECTION);
      this.#dataSectionId = options.dataSectionId || DEFAULT_DATA_SECTION;
      this.#ttl = NmsUtils.toNumber(options.cacheTtl, DEFAULT_TTL_MINUTES) * 60 * 1000;
      this.#debug = Boolean(options.debug);
      this.#scope = NmsUtils.hash(this.#rootUrl + '|' + this.#collectionHandle + '|' + this.#dataSectionId);
    }

    /** Fetch every product in the family that carries the opposite-gender tag. */
    async fetchFamily(familyTagHandle, oppositeTagHandle) {
      if (!familyTagHandle || !oppositeTagHandle) return [];

      const tagPath = familyTagHandle + '+' + oppositeTagHandle;
      const key = CACHE_PREFIX + this.#scope + ':' + NmsUtils.hash(tagPath);

      const cached = this.#readCache(key);
      if (cached) return cached;

      if (NmsCollectionClient.#pending.has(key)) {
        return NmsCollectionClient.#pending.get(key);
      }

      const url = this.#buildUrl(tagPath);
      const self = this;

      const request = this.#execute(url)
        .then(function (products) {
          self.#writeCache(key, products);
          return products;
        })
        .finally(function () {
          NmsCollectionClient.#pending.delete(key);
        });

      NmsCollectionClient.#pending.set(key, request);

      return request;
    }

    #buildUrl(tagPath) {
      const base = NmsUtils.joinUrl(this.#rootUrl, 'collections/' + this.#collectionHandle + '/' + tagPath);
      // Keep the "+" literal; only the query string is encoded.
      return base + '?section_id=' + encodeURIComponent(this.#dataSectionId);
    }

    async #execute(url, attempt) {
      const tries = attempt || 0;

      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: { Accept: 'text/html' },
          credentials: 'same-origin'
        });

        if (!response.ok) {
          if (this.#isRetryable(response.status) && tries < MAX_RETRIES) {
            await NmsUtils.sleep(RETRY_BASE_DELAY * Math.pow(2, tries));
            return this.#execute(url, tries + 1);
          }
          throw new Error('Collection request responded with ' + response.status);
        }

        const html = await response.text();
        return this.#parse(html);
      } catch (error) {
        if (tries < MAX_RETRIES && error instanceof TypeError) {
          await NmsUtils.sleep(RETRY_BASE_DELAY * Math.pow(2, tries));
          return this.#execute(url, tries + 1);
        }
        this.#log('Collection request failed', error);
        throw error;
      }
    }

    #parse(html) {
      let doc;
      try {
        doc = new DOMParser().parseFromString(html, 'text/html');
      } catch (error) {
        this.#log('Unable to parse collection HTML', error);
        return [];
      }

      const script = doc.getElementById(DATA_SCRIPT_ID);
      if (!script) {
        this.#log('Data script not found. Is the data section installed?');
        return [];
      }

      let parsed;
      try {
        parsed = JSON.parse(script.textContent);
      } catch (error) {
        this.#log('Invalid product JSON', error);
        return [];
      }

      return Array.isArray(parsed) ? parsed : [];
    }

    #isRetryable(status) {
      return status === 429 || status >= 500;
    }

    #readCache(key) {
      if (NmsCollectionClient.#memory.has(key)) {
        return NmsCollectionClient.#memory.get(key);
      }

      try {
        const stored = window.sessionStorage.getItem(key);
        if (!stored) return null;

        const entry = JSON.parse(stored);
        if (!entry || typeof entry.t !== 'number' || Date.now() - entry.t > this.#ttl) {
          window.sessionStorage.removeItem(key);
          return null;
        }

        NmsCollectionClient.#memory.set(key, entry.d);
        return entry.d;
      } catch (error) {
        return null;
      }
    }

    #writeCache(key, data) {
      NmsCollectionClient.#memory.set(key, data);

      try {
        window.sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), d: data }));
      } catch (error) {
        /* storage full or unavailable - memory cache still applies */
      }
    }

    #log() {
      if (!this.#debug) return;
      console.warn.apply(console, ['[nms-gender-toggle]'].concat(Array.prototype.slice.call(arguments)));
    }
  }

  /* ------------------------------------------------------------------ *
   * Matching engine
   * ------------------------------------------------------------------ */

  class NmsGenderMatcher {
    #client;

    constructor(client) {
      this.#client = client;
    }

    async findOpposite(context) {
      const products = await this.#client.fetchFamily(context.familyTagHandle, context.oppositeTagHandle);

      const candidates = products.filter(
        function (product) {
          return this.#isCandidate(product, context);
        }.bind(this)
      );

      if (!candidates.length) return null;

      // Priority 1 - exact opposite handle within the family.
      const handleMatch = this.#matchByHandle(candidates, context);
      if (handleMatch) return handleMatch;

      // Priorities 2-4 - colour score, then availability, then order.
      const ranked = this.#rank(candidates, context);
      const best = ranked[0];

      if (best.product.available) {
        return this.#result(best.product, best.score > 0 ? 'color' : 'family', context, best.score);
      }

      // Best on score is out of stock: prefer any available product instead.
      const available = ranked.find(function (entry) {
        return entry.product.available;
      });

      if (available) {
        return this.#result(available.product, available.score > 0 ? 'color' : 'family', context, available.score);
      }

      return this.#result(best.product, 'family-unavailable', context, best.score);
    }

    #matchByHandle(candidates, context) {
      const wanted = NmsHandle.opposites(context.handle, context.gender);
      if (!wanted.length) return null;

      const lookup = new Set(wanted);
      const matches = candidates.filter(function (product) {
        return lookup.has(product.handle);
      });

      if (!matches.length) return null;

      const available = matches.find(function (product) {
        return product.available;
      });

      if (available) return this.#result(available, 'handle', context);
      return this.#result(matches[0], 'handle-unavailable', context);
    }

    #rank(candidates, context) {
      const scored = candidates.map(function (product, index) {
        const colors = NmsColor.resolve(product);
        return {
          product: product,
          index: index,
          colors: colors,
          score: NmsColor.score(context.colors, colors),
          exact: NmsColor.isExactMatch(context.colors, colors)
        };
      });

      scored.sort(function (a, b) {
        if (a.product.available !== b.product.available) {
          return a.product.available ? -1 : 1;
        }
        if (a.score !== b.score) return b.score - a.score;
        if (a.exact !== b.exact) return a.exact ? -1 : 1;
        return a.index - b.index;
      });

      return scored;
    }

    #isCandidate(product, context) {
      if (!product || !product.handle) return false;
      if (product.handle === context.handle) return false;
      if (context.id && product.id && String(product.id) === String(context.id)) return false;
      return true;
    }

    #result(product, strategy, context, score) {
      return {
        product: product,
        strategy: strategy,
        score: typeof score === 'number' ? score : null,
        url: NmsGenderMatcher.buildUrl(product, context)
      };
    }

    static buildUrl(product, context) {
      if (product.url) return product.url;
      if (product.handle) {
        return NmsUtils.joinUrl(NmsUtils.joinUrl(context.rootUrl || '/', 'products'), product.handle);
      }
      return '#';
    }
  }

  /* ------------------------------------------------------------------ *
   * Custom element
   * ------------------------------------------------------------------ */

  class NmsGenderToggle extends HTMLElement {
    #context = null;
    #client = null;
    #matcher = null;
    #target = null;
    #status = null;
    #destroyed = false;
    #started = false;
    #debug = false;

    connectedCallback() {
      if (this.#started) return;
      this.#started = true;
      this.#destroyed = false;
      this.#init();
    }

    disconnectedCallback() {
      this.#destroyed = true;
    }

    async #init() {
      this.#debug = NmsUtils.toBoolean(this.dataset.debug, false);
      this.#target = this.querySelector('[data-nms-gender-target]');
      this.#status = this.querySelector('[data-nms-gender-status]');
      this.#context = this.#readContext();

      if (!this.#context || !this.#target) {
        this.#hide('invalid-context');
        return;
      }

      this.#client = new NmsCollectionClient({
        rootUrl: this.#context.rootUrl,
        collectionHandle: this.dataset.collectionHandle,
        dataSectionId: this.dataset.dataSectionId,
        cacheTtl: this.dataset.cacheTtl,
        debug: this.#debug
      });

      this.#matcher = new NmsGenderMatcher(this.#client);

      try {
        const match = await this.#matcher.findOpposite(this.#context);
        if (this.#destroyed) return;

        if (!match || !match.product) {
          this.#hide('no-match');
          return;
        }

        this.#apply(match);
      } catch (error) {
        this.#log('Unable to resolve the opposite gender product', error);
        this.#hide('error');
      }
    }

    #readContext() {
      const script = this.querySelector('[data-nms-gender-context]');
      if (!script) return null;

      let raw;
      try {
        raw = JSON.parse(script.textContent);
      } catch (error) {
        this.#log('Invalid context JSON', error);
        return null;
      }

      if (!raw || !raw.handle || !raw.familyTagHandle || !raw.oppositeTagHandle || !raw.gender) {
        return null;
      }

      const colors = NmsColor.fromMetafield(raw.colors);

      return {
        id: raw.id || null,
        handle: raw.handle,
        title: raw.title || '',
        url: raw.url || '',
        gender: raw.gender,
        oppositeGender: raw.oppositeGender,
        familyTagHandle: raw.familyTagHandle,
        oppositeTagHandle: raw.oppositeTagHandle,
        rootUrl: raw.rootUrl || '/',
        colors: colors.length ? colors : NmsColor.fromTitle(raw.title || '')
      };
    }

    #apply(match) {
      const link = this.#target;

      link.href = match.url;
      link.removeAttribute('aria-disabled');
      link.removeAttribute('tabindex');
      link.dataset.nmsStrategy = match.strategy;
      link.dataset.nmsHandle = match.product.handle;

      this.dataset.nmsState = 'ready';
      this.dataset.nmsAvailability = match.product.available ? 'available' : 'unavailable';
      this.hidden = false;

      if (this.#status) {
        this.#status.textContent = match.product.title || '';
      }

      this.#log('Matched', match.strategy, match.product.handle, 'score:', match.score);

      this.dispatchEvent(
        new CustomEvent('nms:gender-toggle:resolved', {
          bubbles: true,
          detail: { match: match, context: this.#context }
        })
      );
    }

    #hide(reason) {
      this.dataset.nmsState = 'empty';
      this.dataset.nmsReason = reason;
      this.hidden = true;

      this.dispatchEvent(
        new CustomEvent('nms:gender-toggle:hidden', {
          bubbles: true,
          detail: { reason: reason }
        })
      );
    }

    #log() {
      if (!this.#debug) return;
      console.warn.apply(console, ['[nms-gender-toggle]'].concat(Array.prototype.slice.call(arguments)));
    }
  }

  window.customElements.define(TAG_NAME, NmsGenderToggle);

  window.NmsGenderToggle = {
    Element: NmsGenderToggle,
    Client: NmsCollectionClient,
    Matcher: NmsGenderMatcher,
    Color: NmsColor,
    Handle: NmsHandle
  };
})();
